import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = ({ language }) => {
  const isRo = language === 'ro';
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#faf9f7] py-24 sm:py-32">
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-4xl">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-[#334d6e] hover:text-[#0D1B3E] font-body text-sm mb-8 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          {isRo ? 'Înapoi la pagina principală' : 'Back to Home'}
        </button>

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0D1B3E] mb-12">
          {isRo ? 'Politica de Confidențialitate (GDPR)' : 'Privacy Policy (GDPR)'}
        </h1>

        <div className="prose prose-lg max-w-none font-body text-[#334d6e] space-y-8">
          <p className="text-sm" data-testid="privacy-last-updated">{isRo ? 'Ultima actualizare' : 'Last updated'}: 08.10.2026</p>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              1. {isRo ? 'Operator de Date' : 'Data Controller'}
            </h2>
            <p><strong>Quantive Endeavors SRL</strong><br />
            Str. Avram Iancu nr. 56, ap. 2, Brașov<br />
            CUI 54042200<br />
            Email: contact@montreon.ro</p>
            <p>{isRo 
              ? 'Procesăm datele personale în conformitate cu Regulamentul UE 2016/679 (GDPR).' 
              : 'We process personal data in compliance with EU Regulation 2016/679 (GDPR).'}</p>
          </section>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              2. {isRo ? 'Ce Date Colectăm' : 'What Data We Collect'}
            </h2>
            <p>{isRo ? 'Putem colecta:' : 'We may collect:'}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{isRo ? 'Nume complet' : 'Full name'}</li>
              <li>{isRo ? 'Adresă de email' : 'Email address'}</li>
              <li>{isRo ? 'Număr de telefon' : 'Phone number'}</li>
              <li>{isRo ? 'Adresă' : 'Address'}</li>
              <li>{isRo ? 'Informații despre permisul de conducere' : 'Driving license information'}</li>
              <li>{isRo ? 'Detalii de plată' : 'Payment details'}</li>
              <li>{isRo ? 'Adresă IP' : 'IP address'}</li>
              <li>{isRo ? 'Detalii rezervare' : 'Booking details'}</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              3. {isRo ? 'Scopul Procesării' : 'Purpose of Processing'}
            </h2>
            <p>{isRo ? 'Procesăm datele personale pentru:' : 'We process personal data to:'}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{isRo ? 'Confirmarea rezervărilor' : 'Confirm bookings'}</li>
              <li>{isRo ? 'Întocmirea contractelor de prestări servicii' : 'Draft service agreements'}</li>
              <li>{isRo ? 'Procesarea plăților' : 'Process payments'}</li>
              <li>{isRo ? 'Oferirea suportului pentru clienți' : 'Provide customer support'}</li>
              <li>{isRo ? 'Respectarea obligațiilor legale' : 'Comply with legal obligations'}</li>
              <li>{isRo ? 'Îmbunătățirea serviciilor noastre' : 'Improve our services'}</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              4. {isRo ? 'Temeiul Legal' : 'Legal Basis'}
            </h2>
            <p>{isRo ? 'Procesarea se bazează pe:' : 'Processing is based on:'}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{isRo ? 'Executarea contractului' : 'Contract performance'}</li>
              <li>{isRo ? 'Obligații legale' : 'Legal obligations'}</li>
              <li>{isRo ? 'Interes legitim' : 'Legitimate interest'}</li>
              <li>{isRo ? 'Consimțământul utilizatorului (pentru marketing)' : 'User consent (for marketing)'}</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              5. {isRo ? 'Păstrarea Datelor' : 'Data Retention'}
            </h2>
            <p>{isRo ? 'Datele personale sunt păstrate:' : 'Personal data is retained:'}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{isRo ? 'Pe durata relației contractuale' : 'For the duration of the contractual relationship'}</li>
              <li>{isRo ? 'Conform cerințelor contabile și legale' : 'As required by accounting and legal regulations'}</li>
              <li>{isRo ? 'Până la retragerea consimțământului (pentru marketing)' : 'Until withdrawal of consent (for marketing)'}</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              6. {isRo ? 'Partajarea Datelor' : 'Data Sharing'}
            </h2>
            <p>{isRo ? 'Putem partaja date cu:' : 'We may share data with:'}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{isRo ? 'Procesatori de plăți' : 'Payment processors'}</li>
              <li>{isRo ? 'Furnizori de servicii contabile' : 'Accounting providers'}</li>
              <li>{isRo ? 'Autorități legale (când este necesar)' : 'Legal authorities (when required)'}</li>
              <li>{isRo ? 'Furnizori IT și de hosting' : 'IT and hosting providers'}</li>
            </ul>
            <p>{isRo ? 'Nu vindem datele personale.' : 'We do not sell personal data.'}</p>
          </section>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              7. {isRo ? 'Drepturile Dumneavoastră' : 'Your Rights'}
            </h2>
            <p>{isRo ? 'Conform GDPR, aveți dreptul de a:' : 'Under GDPR, you have the right to:'}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{isRo ? 'Accesa datele dumneavoastră' : 'Access your data'}</li>
              <li>{isRo ? 'Rectifica datele incorecte' : 'Rectify inaccurate data'}</li>
              <li>{isRo ? 'Solicita ștergerea' : 'Request deletion'}</li>
              <li>{isRo ? 'Restricționa procesarea' : 'Restrict processing'}</li>
              <li>{isRo ? 'Portabilitatea datelor' : 'Data portability'}</li>
              <li>{isRo ? 'Vă opune procesării' : 'Object to processing'}</li>
              <li>{isRo ? 'Retrage consimțământul' : 'Withdraw consent'}</li>
            </ul>
            <p>{isRo ? 'Cererile pot fi trimise la:' : 'Requests can be sent to:'} <a href="mailto:contact@montreon.ro" className="text-[#10B981] hover:underline">contact@montreon.ro</a></p>
          </section>

          <section>
            <h2 className="font-display text-xl text-[#0D1B3E] mt-8 mb-4">
              8. {isRo ? 'Securitatea Datelor' : 'Data Security'}
            </h2>
            <p>{isRo 
              ? 'Implementăm măsuri tehnice și organizatorice pentru a proteja datele personale împotriva accesului neautorizat sau pierderii.' 
              : 'We implement technical and organizational measures to protect personal data against unauthorized access or loss.'}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
